import { useRef } from 'react';
import { Pill } from '../Pill';
import { FlexBox } from '@ardoq/layout';
import { OperatorButton, OperatorOption } from '../OperatorButton';
import { ValueButton } from '../ValueButton';
import { Icon, IconSize, MinimalIconProps } from '@ardoq/icons';
import { PillButton } from '../PillButton';
import { CloseFilterPillIcon } from '../CloseFilterPillIcon';
import {
  DatePopoverProps,
  ListPopoverProps,
  NumberPopoverProps,
} from '../Popover';
import { HierarchicalListPopoverProps } from '../HierarchicalListPopover';
import { Text } from '@ardoq/typography';
import { colors } from '@ardoq/design-tokens';
import { SettingsButton, SettingsButtonProps } from './SettingsButton';
import { pluralize } from '@ardoq/common-helpers';
import { WithPopover, Overlay } from '@ardoq/popovers';
import { noop } from 'lodash';
import { Locale } from '@ardoq/locale';
import { DashboardPillLabels } from './labels';
import { ButtonSize, Button } from '@ardoq/button';
import styled from 'styled-components';

type DashboardFilterPillLabelProps = {
  warnings: number;
  label: string;
  icon: MinimalIconProps;
} & (
  | { isInReadOnlyMode: true }
  | { isInReadOnlyMode: false; enterLinkModeForFilter: VoidFunction }
);

const InlineButton = styled(Button)`
  vertical-align: middle;
`;

const DashboardFilterPillLabel = (props: DashboardFilterPillLabelProps) => {
  const popoverContent = props.isInReadOnlyMode
    ? `${props.warnings} linked ${pluralize(
        'chart',
        props.warnings
      )} can't use this filter. `
    : () => (
        <div style={{ maxWidth: '270px' }}>
          {props.warnings} linked {pluralize('chart', props.warnings)} can’t be
          filtered. Select{' '}
          <InlineButton
            buttonSize={ButtonSize.SMALL}
            onClick={() => {
              props.enterLinkModeForFilter();
            }}
          >
            Edit linked charts
          </InlineButton>{' '}
          and try linking the column to the filter again. <br />
          <br />
          If the issue persists, check the source report. The column might be
          missing or its type changed.
        </div>
      );

  return (
    <WithPopover content={props.warnings > 0 ? popoverContent : null}>
      <FlexBox
        gap="xsmall"
        align="center"
        paddingX="small"
        paddingY="xsmall"
        backgroundColor={props.warnings ? 'surfaceWarning' : undefined}
      >
        <Icon
          iconName={props.icon?.name}
          iconSize={IconSize.SMALL}
          color={props.icon.color ?? colors.icon}
        />
        <Text color={props.warnings ? 'textWarning' : 'textDefault'}>
          {props.label}
        </Text>
      </FlexBox>
    </WithPopover>
  );
};
type ClearButtonProps =
  | { showClearButton: true; clearFilter: VoidFunction }
  | { showClearButton: false };

export type DashboardFilterPillNumberPopoverProps = {
  type: 'number';
  value: number | null;
  updateDashboardFilterValue: (values: number) => void;
  label: string;
};

export type DashboardFilterPillDatePopoverProps = {
  type: 'date';
  value: string | null;
  updateDashboardFilterValue: (values: string | null) => void;
  label: string;
  locale: Locale;
};

export type DashboardFilterPillPopoverContentProps =
  | ListPopoverProps
  | HierarchicalListPopoverProps
  | DashboardFilterPillDatePopoverProps
  | DashboardFilterPillNumberPopoverProps;

export type DashboardFilterPillValueProps = {
  popover: {
    isOpen: boolean;
    content:
      | ListPopoverProps
      | HierarchicalListPopoverProps
      | NumberPopoverProps
      | DatePopoverProps;
    onClose: VoidFunction;
  };
  filterLabel: string;
  button: {
    label: string;
    isDisabled: boolean;
    isPopoverOpen: boolean;
    onClick: VoidFunction;
  } & ClearButtonProps;
};

export type DashboardFilterOperatorButtonProps =
  | { hide: true }
  | {
      hide?: false;
      button: {
        label: string;
        isPopoverOpen: boolean;
        onClick: VoidFunction;
      };
      popover: {
        isOpen: boolean;
        options: OperatorOption[];
        onClose: VoidFunction;
      };
    };

export type DashboardFilterPillProps = {
  label: DashboardFilterPillLabelProps;
  operatorProps: DashboardFilterOperatorButtonProps;
  valueProps: DashboardFilterPillValueProps;
  showValueButton: boolean;
  preventClickThrough?: boolean;
  labels?: DashboardPillLabels;
} & (
  | { isInReadOnlyMode: true }
  | {
      isInReadOnlyMode: false;
      settings: SettingsButtonProps;
    }
);

export const DashboardFilterPillView = ({
  operatorProps,
  label,
  valueProps,
  showValueButton,
  preventClickThrough = false,
  labels,
  ...props
}: DashboardFilterPillProps) => {
  const valueButtonAnchorRef = useRef<HTMLDivElement | null>(null);

  return (
    <Pill>
      <Overlay
        isOpen={
          preventClickThrough &&
          (valueProps.popover.isOpen ||
            (!operatorProps.hide && operatorProps.popover.isOpen))
        }
        onClose={noop}
      />
      <DashboardFilterPillLabel {...label} />
      {!operatorProps.hide && (
        <FlexBox align="end" ref={valueButtonAnchorRef}>
          <OperatorButton
            type="button"
            button={operatorProps.button}
            popover={operatorProps.popover}
          />
        </FlexBox>
      )}
      {showValueButton && (
        <FlexBox
          gap="none"
          align="center"
          ref={operatorProps.hide ? valueButtonAnchorRef : null}
        >
          <ValueButton {...valueProps} />
          {valueProps.button.showClearButton && (
            <PillButton
              $isActive={false}
              onClick={valueProps.button.clearFilter}
              aria-label="clear filter"
              style={{ padding: '5px 2px' }}
            >
              <CloseFilterPillIcon />
            </PillButton>
          )}
        </FlexBox>
      )}
      {!props.isInReadOnlyMode && (
        <SettingsButton
          {...props.settings}
          labels={props.settings.labels ?? labels}
        />
      )}
    </Pill>
  );
};
